import { createServerFn } from "@tanstack/react-start";
import { Result } from "better-result";
import { z } from "zod/v4";
import { toResult } from "#/modules/util.auth/api-errors";
import { authMiddleware } from "#/modules/util.auth/middleware.auth";
import { toastErrorMiddleware } from "#/modules/util.errors/middleware.toast-errors";
import { apiFailure } from "#/modules/util.errors/api-failure.server";
import { toFieldErrors, translateApiError } from "#/modules/util.errors/translate";
import { client } from "#/modules/util.fetch-client/fetch-client.server";
import { m } from "#/paraglide/messages";
import type { components } from "#/schema";

/** Tiptap serializes an empty document as "<p></p>" — count only real text content. */
export const hasVisibleContent = (html: string) =>
  html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim().length > 0;

// Attachments upload separately and travel as fileIds, so the HTML body is plain rich text —
// a generous ceiling still guards against pathological pastes.
const MAX_CONTENT_LENGTH = 50_000;

export const MAX_TICKET_FILES = 5;

export const ticketSubjectSchema = z
  .string()
  .trim()
  .min(1, { error: m["validation.required"]() })
  .max(150, { error: m["tickets.subject_too_long"]() });

const ticketFileIdsSchema = z
  .array(z.uuid())
  .max(MAX_TICKET_FILES, { error: m["tickets.too_many_files"]() });

/** Client-side draft schema: content is optional as long as at least one attachment is present. */
export const createTicketDraftSchema = z.object({
  subject: ticketSubjectSchema,
  content: z.string().max(MAX_CONTENT_LENGTH, { error: m["tickets.too_large"]() }),
  fileIds: ticketFileIdsSchema,
});

export const createTicketFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(createTicketDraftSchema)
  .handler(async ({ data }) => {
    // The type toggle was dropped from the UI — every ticket is a question now; the backend
    // field lives on with a single value.
    const body: components["schemas"]["CreateTicketCommand"] = {
      subject: data.subject,
      type: "Question",
      content: data.content,
      fileIds: data.fileIds,
    };

    const result = toResult(await client.POST("/user/tickets", { body }));

    if (Result.isError(result)) {
      const fieldErrors = toFieldErrors(result.error);
      return fieldErrors ? { fieldErrors } : { formError: translateApiError(result.error) };
    }

    return result.value;
  });

/** GET /user/tickets — the current user's tickets, newest activity first (backend-sorted). */
export const getTicketsFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async () => {
    const result = toResult(await client.GET("/user/tickets"));
    if (Result.isError(result)) throw result.error;
    return result.value.tickets;
  });

/** GET /user/tickets/{id} — full ticket detail including the message thread. */
export const getTicketFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(z.object({ id: z.uuid() }))
  .handler(async ({ data }) => {
    const result = toResult(
      await client.GET("/user/tickets/{id}", { params: { path: { id: data.id } } }),
    );
    if (Result.isError(result)) throw result.error;
    return result.value;
  });

// Content alone is optional here — same as `createTicketDraftSchema` — as long as at least one
// attachment is present. The backend's AddTicketMessageCommand validator only requires Content to
// be non-empty, and tiptap's empty-editor output ("<p></p>") already satisfies that trivially, so
// there's nothing left for a schema-level refine to enforce; the caller (`TicketComposer`) is
// responsible for the actual "text OR ≥1 attachment" guard before submitting.
export const addTicketMessageSchema = z.object({
  id: z.uuid(),
  content: z.string().max(MAX_CONTENT_LENGTH, { error: m["tickets.too_large"]() }),
  fileIds: ticketFileIdsSchema,
});

export const addTicketMessageFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(addTicketMessageSchema)
  .handler(async ({ data }) => {
    const body: components["schemas"]["AddTicketMessageRequest"] = {
      content: data.content,
      fileIds: data.fileIds,
    };

    const result = toResult(
      await client.POST("/user/tickets/{id}/messages", {
        params: { path: { id: data.id } },
        body,
      }),
    );

    if (Result.isError(result)) {
      const fieldErrors = toFieldErrors(result.error);
      return fieldErrors ? { fieldErrors } : { formError: translateApiError(result.error) };
    }
  });

/**
 * PUT /user/tickets/{id}/messages/{messageId} — the user edits one of their own messages. `fileIds`
 * is the full attachment set after the edit: files left out are removed.
 */
export const editTicketMessageFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(addTicketMessageSchema.extend({ messageId: z.uuid() }))
  .handler(async ({ data }) => {
    const result = toResult(
      await client.PUT("/user/tickets/{id}/messages/{messageId}", {
        params: { path: { id: data.id, messageId: data.messageId } },
        body: { content: data.content, fileIds: data.fileIds },
      }),
    );

    if (Result.isError(result)) {
      const fieldErrors = toFieldErrors(result.error);
      return fieldErrors ? { fieldErrors } : { formError: translateApiError(result.error) };
    }
  });

/** DELETE /user/tickets/{id}/messages/{messageId} — the user deletes one of their follow-ups. */
export const deleteTicketMessageFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(z.object({ id: z.uuid(), messageId: z.uuid() }))
  .handler(async ({ data }) => {
    const result = toResult(
      await client.DELETE("/user/tickets/{id}/messages/{messageId}", {
        params: { path: { id: data.id, messageId: data.messageId } },
      }),
    );
    if (Result.isError(result)) return apiFailure(result.error);
  });

/** POST /user/tickets/{id}/read — clears the unread flag for this ticket's admin replies. */
export const markTicketReadFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(z.object({ id: z.uuid() }))
  .handler(async ({ data }) => {
    const result = toResult(
      await client.POST("/user/tickets/{id}/read", { params: { path: { id: data.id } } }),
    );
    if (Result.isError(result)) return apiFailure(result.error);
  });

/** POST /user/tickets/{id}/close — the user closes their own ticket; replying reopens it. */
export const closeTicketFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(z.object({ id: z.uuid() }))
  .handler(async ({ data }) => {
    const result = toResult(
      await client.POST("/user/tickets/{id}/close", { params: { path: { id: data.id } } }),
    );
    if (Result.isError(result)) return apiFailure(result.error);
  });

/**
 * Multipart upload for a ticket attachment. Mirrors `validateImportFile`'s
 * FormData → `bodySerializer` pattern (see `school.import/api.import-file.server.ts`): the
 * client posts a plain FormData server-fn payload, and the handler re-wraps the file into the
 * shape openapi-fetch's `bodySerializer` expects for a multipart request.
 */
export const uploadTicketFileFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator((data: FormData) => {
    if (!(data instanceof FormData)) {
      throw new Error("uploadTicketFileFn expects FormData");
    }
    return data;
  })
  .handler(async ({ data: formData }) => {
    const file = formData.get("file");

    const result = toResult(
      await client.POST("/user/tickets/files", {
        // The typed body is a placeholder: the serializer below sends the uploaded entry itself.
        body: { file: "" },
        bodySerializer: () => {
          const uploadFormData = new FormData();
          if (file) uploadFormData.set("file", file);
          return uploadFormData;
        },
      }),
    );

    if (Result.isError(result)) {
      return { formError: translateApiError(result.error) };
    }

    return { file: result.value.file };
  });
